import { Button, Drawer, Form, Typography, Input, Divider, message, Spin, Space } from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import React, { useEffect, useState } from "react"
import { db } from "../../../firebase/config"
import { collection, doc, getDocs, query, updateDoc, where, writeBatch } from "firebase/firestore"

const { Link, Text } = Typography

export default function EditManufacturerDrawer(props) {
  const [open, setOpen] = useState(false)
  const [editForm] = Form.useForm()
  const [isUpdating, setIsUpdating] = useState(false)

  const data = props.data

  useEffect(() => {
    // Support both old flat structure and new object structure
    let bankAccounts = []

    if (data.bankAccounts && Array.isArray(data.bankAccounts)) {
      // New object structure
      bankAccounts = data.bankAccounts
    } else {
      // Old flat structure - migrate to new
      if (data.bankName || data.bankCode || data.accountNumber) {
        bankAccounts.push({
          bankName: data.bankName || "",
          bankCode: data.bankCode || "",
          accountNumber: data.accountNumber || "",
        })
      }
      if (data.bankName2 || data.bankCode2 || data.accountNumber2) {
        bankAccounts.push({
          bankName: data.bankName2 || "",
          bankCode: data.bankCode2 || "",
          accountNumber: data.accountNumber2 || "",
        })
      }
      if (data.bankName3 || data.bankCode3 || data.accountNumber3) {
        bankAccounts.push({
          bankName: data.bankName3 || "",
          bankCode: data.bankCode3 || "",
          accountNumber: data.accountNumber3 || "",
        })
      }
    }

    if (bankAccounts.length === 0) {
      bankAccounts.push({ bankName: "", bankCode: "", accountNumber: "" })
    }

    const defaultValues = {
      company: data.company,
      vat: data.vat,
      reg: data.reg,
      legalCity: data.legalCity,
      legalPC: data.legalPC,
      legalAddress: data.legalAddress,
      deliveryCity: data.deliveryCity,
      deliveryPC: data.deliveryPC,
      deliveryAddress: data.deliveryAddress,
      bankAccounts: bankAccounts,
      comment: data.comment,
      invoicePrefix: data.invoicePrefix,
      logoUrl: data.logoUrl || "",
    }

    editForm.setFieldsValue(defaultValues)
  }, [data, editForm])

  const showDrawer = () => {
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }

  const onFinish = async (values) => {
    try {
      setIsUpdating(true)

      // Use new object structure directly
      const updateData = {
        ...values,
        bankAccounts: values.bankAccounts || [],
      }

      await updateManufacturer(updateData)
      await updateClientsManufacturer(props.data.id, updateData)
      await updateOrdersManufacturer(props.data.id, updateData)

      message.success("All updates completed successfully!")
    } catch (error) {
      message.error("Error updating manufacturer: " + error.message)
    } finally {
      setIsUpdating(false)
      setOpen(false)
    }
  }

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo)
  }

  // Update the manufacturer document in the "manufacturers" collection.
  const updateManufacturer = async (data) => {
    const ref = doc(db, "manufacturers", props.data.id)
    await updateDoc(ref, data)
    console.log("Updated manufacturer doc in manufacturers collection")
  }

  // Update all clients where manufacturer.id matches.
  const updateClientsManufacturer = async (manufacturerId, updatedData) => {
    const clientsRef = collection(db, "clients")
    const q = query(clientsRef, where("manufacturer.id", "==", manufacturerId))
    const querySnapshot = await getDocs(q)

    let batch = writeBatch(db)
    let writesCount = 0

    for (const clientDoc of querySnapshot.docs) {
      const clientRef = doc(db, "clients", clientDoc.id)
      batch.update(clientRef, {
        manufacturer: {
          ...updatedData,
        },
      })
      writesCount++
      // Commit a batch every 500 writes.
      if (writesCount === 500) {
        await batch.commit()
        batch = writeBatch(db)
        writesCount = 0
      }
    }
    if (writesCount > 0) {
      await batch.commit()
    }
    console.log(`Updated ${querySnapshot.size} clients successfully.`)
  }

  // Update all orders where manufacturer.id matches.
  const updateOrdersManufacturer = async (manufacturerId, newManufacturerData) => {
    const ordersRef = collection(db, "orders")
    const q = query(ordersRef, where("manufacturer.id", "==", manufacturerId))
    const querySnapshot = await getDocs(q)

    let batch = writeBatch(db)
    let writesCount = 0

    for (const orderDoc of querySnapshot.docs) {
      const orderRef = doc(db, "orders", orderDoc.id)
      // Update both top-level "manufacturer" and nested "client.manufacturer"
      batch.update(orderRef, {
        manufacturer: {
          ...newManufacturerData,
        },
        "client.manufacturer": {
          ...newManufacturerData,
        },
      })
      writesCount++
      // Commit a batch every 500 writes.
      if (writesCount === 500) {
        await batch.commit()
        batch = writeBatch(db)
        writesCount = 0
      }
    }
    if (writesCount > 0) {
      await batch.commit()
    }
    console.log(`Updated ${querySnapshot.size} orders successfully.`)
  }


  return (
    <>
      <Link style={{ color: "#52c41a" }} onClick={showDrawer}>
        edit
      </Link>
      <Drawer width={650} forceRender placement="left" onClose={onClose} open={open}>
        <Divider>Editing : {data.company}</Divider>
        {isUpdating && (
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <Spin tip="Updating..." />
          </div>
        )}
        <Form
          form={editForm}
          name="edit-manufacturer"
          size="small"
          style={{ width: "100%", padding: 25 }}
          layout="horizontal"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off">
          <Form.Item
            label="Manufacturer Company"
            name="company"
            rules={[
              {
                required: true,
                message: "Please input your manufacturer company!",
              },
            ]}>
            <Input placeholder="Manufacturer company" />
          </Form.Item>
          <Form.Item
            label="Vat Number"
            name="vat"
            rules={[
              {
                required: true,
                message: "Please input your vat!",
              },
            ]}>
            <Input placeholder="vat" />
          </Form.Item>
          <Form.Item
            label="Reg Number"
            name="reg"
            rules={[
              {
                required: true,
                message: "Please input your reg!",
              },
            ]}>
            <Input placeholder="reg" />
          </Form.Item>
          <Divider>
            <Text style={{ fontSize: 14 }}> Legal Address</Text>
          </Divider>
          <Form.Item
            label="Legal city"
            name="legalCity"
            rules={[
              {
                required: true,
                message: "Please input your legal city!",
              },
            ]}>
            <Input placeholder="Legal city" />
          </Form.Item>
          <Form.Item
            label="Legal postal code"
            name="legalPC"
            rules={[
              {
                required: true,
                message: "Please input your postal code!",
              },
            ]}>
            <Input placeholder="LV-1003" />
          </Form.Item>
          <Form.Item
            label="Legal address"
            name="legalAddress"
            rules={[
              {
                required: true,
                message: "Please input your legal address!",
              },
            ]}>
            <Input placeholder="Jelgavas iela 5b" />
          </Form.Item>

          <Divider>
            <Text style={{ fontSize: 14 }}> Delivery Address</Text>
          </Divider>

          <Form.Item
            label="Delivery City"
            name="deliveryCity"
            rules={[
              {
                required: true,
                message: "Please input your delivery city!",
              },
            ]}>
            <Input placeholder="Delivery city" />
          </Form.Item>
          <Form.Item
            label="Delivery postal code"
            name="deliveryPC"
            rules={[
              {
                required: true,
                message: "Please input your postal code!",
              },
            ]}>
            <Input placeholder="LV-1005" />
          </Form.Item>
          <Form.Item
            label="Delivery address"
            name="deliveryAddress"
            rules={[
              {
                required: true,
                message: "Please input your delivery address!",
              },
            ]}>
            <Input placeholder="Brīvības iela 61b" />
          </Form.Item>

          <Divider>
            <Text style={{ fontSize: 14 }}> Bank Details</Text>
          </Divider>

          <Form.List name="bankAccounts">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid #f0f0f0" }}>
                    <Text strong style={{ fontSize: 12, marginBottom: 8 }}>
                      Bank Account {index + 1}
                    </Text>

                    <Form.Item
                      label="Bank Name"
                      name={[field.name, "bankName"]}
                      rules={[
                        index === 0
                          ? { required: true, message: "Please input bank name!" }
                          : { required: false },
                      ]}>
                      <Input placeholder="AS 'SEB banka'..." />
                    </Form.Item>

                    <Form.Item
                      label="Bank Code"
                      name={[field.name, "bankCode"]}
                      rules={[
                        index === 0
                          ? { required: true, message: "Please input bank code!" }
                          : { required: false },
                      ]}>
                      <Input placeholder="UNLALV2X..." />
                    </Form.Item>

                    <Form.Item
                      label="Account Number"
                      name={[field.name, "accountNumber"]}
                      rules={[
                        index === 0
                          ? { required: true, message: "Please input account number!" }
                          : { required: false },
                      ]}>
                      <Input
                        placeholder="LV95UNLA00500007506..."
                        addonAfter={
                          fields.length > 1 ? (
                            <DeleteOutlined
                              style={{ cursor: "pointer", color: "#ff4d4f" }}
                              onClick={() => remove(field.name)}
                            />
                          ) : null
                        }
                      />
                    </Form.Item>
                  </div>
                ))}
                <Form.Item>
                  <Button type="dashed" block onClick={() => add()} icon={<PlusOutlined />}>
                    Add Bank Account
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item
            label="Invoice prefix"
            name="invoicePrefix"
            rules={[
              {
                required: true,
                message: "Please input your invoice prefix!",
              },
            ]}>
            <Input placeholder="AL" />
          </Form.Item>

          {/* New Form.Item for Logo URL */}
          <Form.Item
            label="Logo URL"
            name="logoUrl"
            rules={[
              {
                required: false,
              },
            ]}>
            <Input placeholder="Enter Logo URL" />
          </Form.Item>

          <Divider>
            <Text style={{ fontSize: 14 }}> Comment</Text>
          </Divider>

          <Form.Item name="comment" label="Comment">
            <Input.TextArea rows={2} placeholder="Manufacturer comment" />
          </Form.Item>

          <Form.Item>
            <Button block type="primary" htmlType="submit">
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  )
}
